<template>
  <div class="wrap">
    <!-- Header -->
    <b-container fluid>
      <b-row v-show="!loading">
        <b-col sm="8"
               md="6"
               lg="4">
          <esg-single-score-card :title="`당해 연도 배출량 (${currentYear}년)`"
                                 :score="formatNumber(currentYearActual, 0)"
                                 score-unit="tCO2e"
                                 :diff="Math.round(currentYearReductionRate * 10) / 10"
                                 :diff-unit="`% ${currentYearTarget >= 0 ? '초과' : '감축'}`"
                                 :color="`${currentYearTarget >= 0 ? 'red' : 'green'}`"
                                 :arrow="false">
          </esg-single-score-card>
        </b-col>
        <b-col sm="8"
               md="6"
               lg="4">
          <esg-single-score-card title="단기 목표 배출량(NDC 충족 - 2030년)"
                                 :score="formatNumber(shortTermNdcTarget, 0)"
                                 score-unit="tCO2e"
                                 color="yellow">
            <template #annotation>
              <b class="mx-2 annotation">* BAU 대비 {{ formatNumber(shortTermReductionRate, 0) }}% 감축</b>
            </template>
          </esg-single-score-card>
        </b-col>
        <b-col sm="8"
               md="6"
               lg="4">
          <esg-single-score-card title="장기 목표 배출량(NDC 충족 - 2050년)"
                                 :score="formatNumber(longTermNdcTarget, 0)"
                                 score-unit="tCO2e"
                                 color="yellow">
            <template #annotation>
              <b class="mx-2 annotation">* BAU 대비 {{ formatNumber(longTermReductionRate, 0) }}% 감축</b>
            </template>
          </esg-single-score-card>
        </b-col>
      </b-row>
      <!-- /Header -->
    </b-container>

    <!-- Chart -->
    <esg-base-chart height="330px">
      <!-- Spinner -->
      <loading :active="loading"
               :z-index="1000"
               :is-full-page="false"/>
      <!-- /Spinner -->

      <apex-chart ref="chart"
                  v-show="!loading"
                  type="line"
                  height="300"
                  :options="chartOptions"
                  :series="series"/>
    </esg-base-chart>
    <!-- /Chart -->

    <!-- Table -->
    <esg-table-without-meta :fields="tableFields"
                            :items="tableItems"
                            v-show="!loading"
                            size="xs"/>
    <!-- /Table -->
  </div>
</template>
<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";
import EsgTableWithoutMeta from "@/components/esg/base/EsgTableWithoutMeta.vue";
import EsgSingleScoreCard from "@/components/esg/base/EsgSingleScoreCard.vue";

export default {
  components: { EsgSingleScoreCard, EsgTableWithoutMeta, EsgBaseChart },
  data() {
    return {
      loading: false,
      firstYear: 2018,
      lastYear: 2050,

      currentYearTarget: 0,
      currentYearActual: 0,
      currentYearReductionRate: 0,
      shortTermNdcTarget: 0,
      longTermNdcTarget: 0,
      shortTermReductionRate: 0,
      longTermReductionRate: 0,

      series: [],
      tableItems: [],
      tableFields: [
        { name: 'item', label: '목표 배출량', list: true, },
        { name: '2018', label: '2018', list: true, formatter: val => this.formatNumber(val, 0) },
        { name: '2030', label: '2030', list: true, formatter: val => this.formatNumber(val, 0) },
        { name: '2050', label: '2050', list: true, formatter: val => this.formatNumber(val, 0) },
      ],
      chartOptions: {
        chart: {
          fontFamily: 'NanumGothic, serif',
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        grid: {
          padding: {
            left: 20,
            right: 40,
          }
        },
        labels: [],
        xaxis: {
          type: 'datetime',
          crosshairs: { show: false },
          tooltip: { enabled: false, },
        },
        yaxis: {
          crosshairs: { show: false },
          title: {
            text: 'NDC 목표 배출량 (tCO2)',
            style: {
              fontSize: 12,
              fontWeight: 'normal'
            }
          },
          labels: {
            formatter: (val) => this.compactFormatNumber(val)
          }
        },
        tooltip: {
          x: {
            formatter: (val) => new Date(val).getFullYear()
          },
          y: {
            formatter: (val) => val ? this.formatNumber(val) + ' tCO2' : null
          }
        },
        dataLabels: {
          enabled: true,
          offsetY: -5,
          style: {
            fontWeight: 400,
            colors: ['#000']
          },
          background: {
            enabled: false,
          },
          formatter: (val, opts) => {
            if (opts.seriesIndex === 1) {
              return '';
            } else {
              const yearsToShowLabelsNdc = [2018, 2030, 2050];  // Specify the years you want to show labels for
              const currentDate = opts.ctx.w.config.labels[opts.dataPointIndex];
              const currentYear = new Date(currentDate).getFullYear();
              if (yearsToShowLabelsNdc.includes(currentYear)) {
                return val ? this.compactFormatNumber(val) : "";
              } else {
                return '';
              }
            }
          }
        },
        markers: {
          size: [0, 4],
          discrete: [
            {
              seriesIndex: 0,
              dataPointIndex: 0,
              size: 5,
              fillColor: this.$store.getters['ghg/colors'][0],
              strokeColor: '#fff',
            },
            {
              seriesIndex: 0,
              dataPointIndex: 2030 - 2018,
              size: 5,
              fillColor: this.$store.getters['ghg/colors'][0],
              strokeColor: '#fff',
            },
            {
              seriesIndex: 0,
              dataPointIndex: 2050 - 2018,
              size: 5,
              fillColor: this.$store.getters['ghg/colors'][0],
              strokeColor: '#fff',
            },
          ],
          hover: {
            size: 6
          }
        },
        stroke: {
          width: [3, 0],
          curve: 'straight'
        },
        legend: {
          position: 'bottom',
          showForSingleSeries: true,
        },
        responsive: [],
      },
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;

        // Construct arguments to fetch actual NDC data
        let years = [2018, 2030, 2050];
        let args = [{ years }];
        let data = new FormData();
        data.append('path', 'esg/target/ndc/main');
        data.append('json', JSON.stringify(args));

        // Fetch NDC data
        this.abortController = new AbortController();
        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });
        if (!response.data.data) {
          this.series = [];
          return;
        }
        let ndc = JSON.parse(response.data.data)[0].data.map(row => row.firmwide_target);

        // Interpolate NDC values. There exists only three values for 2018, 2030, and 2050.
        let interpolatedNdc = this.interpolateValues(ndc, years);

        // Construct arguments to fetch actual GHG emission data
        years = Array.from({ length: this.lastYear - this.firstYear + 1 },
            (_, index) => this.firstYear + index); // Number array from first to last year
        args = [{ years, scopes: [1, 2], groupKeys: ['year'] }];
        data = new FormData();
        data.append('path', 'esg/ghg_emission/main');
        data.append('json', JSON.stringify(args));

        // Fetch actual emission data
        response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });
        if (!response.data.data) {
          this.series = [];
          return;
        }
        let actual = JSON.parse(response.data.data)[0].data;

        this.setSeries(interpolatedNdc, actual, years);
        this.setLabels(years);
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
        this.refreshChart();
      }
    },
    setSeries(interpolatedNdc, actual, years) {
      let actualEmissions = years.map(year => {
        let actualRow = actual.find(row => row.year === year);
        return actualRow ? actualRow.values : null;
      });

      this.series = [
        { name: 'NDC 목표 배출량', data: interpolatedNdc, type: 'line' },
        { name: 'Scope 1 + 2 실제 배출량', data: actualEmissions, type: 'column' }
      ];

      // Set NDC and firmwide targets
      this.bau = interpolatedNdc[0];
      this.shortTermNdcTarget = interpolatedNdc[2030 - this.firstYear];
      this.longTermNdcTarget = interpolatedNdc[2050 - this.firstYear];
      this.currentYearActual = actualEmissions[this.currentYear - this.firstYear];
      this.currentYearTarget = interpolatedNdc[this.currentYear - this.firstYear];

      // Calculate reduction rates displayed in the cards on the top
      this.shortTermReductionRate = 100 * (this.bau - this.shortTermNdcTarget) / this.bau;
      this.longTermReductionRate = 100 * (this.bau - this.longTermNdcTarget) / this.bau;
      this.currentYearReductionRate = 100 * (this.currentYearActual / this.currentYearTarget - 1);

      // Table items
      this.tableItems = [
        {
          'item': '목표 배출량(NDC 충족)',
          '2018': this.bau,
          '2030': this.shortTermNdcTarget,
          '2050': this.longTermNdcTarget,
        },
        {
          'item': '감축비율',
          '2018': '-',
          '2030': this.formatNumber(this.shortTermReductionRate, 0) + '%',
          '2050': this.formatNumber(this.longTermReductionRate, 0) + '%',
        },
      ];
    },
    setLabels(years) {
      this.chartOptions.labels = years.map(year => `${year}-01-01`);
    },
    linearInterpolate(x, x1, x2, y1, y2) {
      return y1 + (x - x1) * (y2 - y1) / (x2 - x1);
    },
    interpolateValues(values, years) {
      let interpolatedValues = [];
      for (let i = 0; i < years.length - 1; i++) {
        let startYear = years[i];
        let endYear = years[i + 1];
        let startValue = values[i];
        let endValue = values[i + 1];

        for (let j = startYear; j < endYear; j++) {
          let interpolatedValue = this.linearInterpolate(j, startYear, endYear, startValue, endValue);
          interpolatedValues.push(interpolatedValue);
        }
      }
      interpolatedValues.push(values[values.length - 1]);  // Append the last value
      return interpolatedValues;
    }
  }
}
</script>

<style scoped>
.wrap {
  width: 100%;
}

.ndc-badge {
  width: 8rem;
}

.target {
  font-size: 1rem;
}

>>> .table-wrapper {
  overflow-x: scroll;
  white-space: nowrap;
}

>>> .table-wrapper table {
  width: auto;
  min-width: 100%;
  margin-bottom: 0;
}

>>> .table td,
>>> .table th {
  font-size: .7rem;
  text-align: center;
}

>>> span.badge.badge-primary {
  padding: .25rem .5rem !important;
  background-color: #5182DE;
  color: #FFF;
  font-weight: normal
}

.annotation {
  font-size: 10px;
  font-weight: 500;
  font-family: NanumGothic;
  color: #888;
}

[class^="col-"] {
  padding-left: 7px;
  padding-right: 7px;
}
</style>
